import { usePage } from '@inertiajs/vue3';
import * as Sentry from '@sentry/vue';

export function debounce(fn, wait) {
    let timer;
    return function(...args) {
        if (timer) {
            clearTimeout(timer); // clear any pre-existing timer
        }
        const context = this; // get the current context
        timer = setTimeout(() => {
            fn.apply(context, args); // call the function if time expires
        }, wait);
    };
}

export function hasPermission (permission, model = null) {
    const page = usePage();

    if (! page.props.auth) {
        return false;
    }

    if (model && model.user_id === page.props.auth.id) {
        return true;
    }

    if ('string' === typeof permission) {
        return page.props.permissions.includes(permission);
    }

    return page.props.permissions.some(p => permission.includes(p));
}

export function canInMod(mod, permission) {
    const page = usePage();

    if (! page.props.auth) {
        return false;
    }

    if (mod.user_id === page.props.auth.id) {
        return true;
    }

    const activePermissions = page.props.modPermissions;

    return activePermissions[permission] ?? false;
}

export function canAnyInMod(mod, permissions) {
    const page = usePage();

    if (! page.props.auth) {
        return false;
    }

    if (mod.user_id === page.props.auth.id) {
        return true;
    }

    return permissions.some(permission => canInMod(mod, permission));
}

const DEFAULT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function safeGetCookie(name, fallback = null) {
    try {
        if ('undefined' === typeof document) {
            return fallback;
        }

        const encodedName = encodeURIComponent(name);
        const cookie = document.cookie
            .split('; ')
            .find(entry => entry.startsWith(`${encodedName}=`));

        if (! cookie) {
            return fallback;
        }

        return decodeURIComponent(cookie.slice(encodedName.length + 1));
    } catch (e) {
        Sentry.captureException(e);

        return fallback;
    }
}

export function safeSetCookie(name, value, maxAge = DEFAULT_COOKIE_MAX_AGE) {
    try {
        if ('undefined' === typeof document) {
            return false;
        }

        const attributes = [
            `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}`,
            'path=/',
            `max-age=${maxAge}`,
            'samesite=lax',
        ];

        if ('undefined' !== typeof window && 'https:' === window.location.protocol) {
            attributes.push('secure');
        }

        document.cookie = attributes.join('; ');

        return true;
    } catch (e) {
        Sentry.captureException(e);

        return false;
    }
}

const UI_PREFERENCE_COOKIE_NAMES = {
    timestampMode: 'timestamp-mode',
};

const UI_PREFERENCE_ALLOWED_VALUES = {
    timestampMode: ['local', 'utc', 'human'],
};

function normalizeUiPreference(key, value, fallback = null) {
    if (undefined === value || null === value) {
        return fallback;
    }

    const allowedValues = UI_PREFERENCE_ALLOWED_VALUES[key];

    if (allowedValues && ! allowedValues.includes(value)) {
        return fallback;
    }

    return value;
}

export function getUiPreference(key, fallback = null) {
    const pageValue = usePage().props.uiPreferences?.[key];

    if (undefined !== pageValue && null !== pageValue) {
        return normalizeUiPreference(key, pageValue, fallback);
    }

    return normalizeUiPreference(
        key,
        safeGetCookie(UI_PREFERENCE_COOKIE_NAMES[key] ?? key, null),
        fallback,
    );
}
